import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["type", "disabled"]
const _hoisted_2 = { key: 2 }

import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

export interface Props {
  text?: string
  loadingText?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  icon?: any
  iconOnly?: boolean
  fullWidth?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseButton',
  props: {
    text: { default: '' },
    loadingText: { default: 'Loading...' },
    type: { default: 'button' },
    variant: { default: 'primary' },
    size: { default: 'md' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    icon: { default: undefined },
    iconOnly: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false }
  },
  emits: ["click"],
  setup(__props: any) {

const LoaderIcon = Loader2

const props = __props



// Computed classes
const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'justify-center',
    'items-center',
    'font-medium',
    'rounded-xl',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'transform',
    'hover:scale-105',
    'active:scale-95'
  ]

  // Size classes
  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-sm'],
    md: ['px-4', 'py-2', 'text-base'],
    lg: ['px-6', 'py-3', 'text-base'],
    xl: ['px-8', 'py-4', 'text-lg']
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-white',
      'bg-primary-gradient',
      'hover:shadow-xl',
      'focus:ring-blue-500',
      'shadow-lg'
    ],
    secondary: [
      'text-gray-700',
      'dark:text-gray-200',
      'bg-white',
      'dark:bg-gray-600',
      'border',
      'border-gray-300',
      'dark:border-gray-500',
      'hover:bg-gray-50',
      'dark:hover:bg-gray-500',
      'focus:ring-gray-500',
      'shadow-sm'
    ],
    danger: ['text-white', 'bg-red-gradient', 'hover:shadow-xl', 'focus:ring-red-500', 'shadow-lg'],
    success: [
      'text-white',
      'bg-green-gradient',
      'hover:shadow-xl',
      'focus:ring-green-500',
      'shadow-lg'
    ],
    warning: [
      'text-white',
      'bg-yellow-gradient',
      'hover:shadow-xl',
      'focus:ring-yellow-500',
      'shadow-lg'
    ],
    ghost: [
      'text-gray-700',
      'dark:text-gray-200',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500'
    ],
    outline: [
      'text-blue-600',
      'dark:text-blue-400',
      'border-2',
      'border-blue-600',
      'dark:border-blue-400',
      'hover:bg-blue-50',
      'dark:hover:bg-blue-900/20',
      'focus:ring-blue-500'
    ]
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // Icon-only classes
  const iconOnlyClasses = props.iconOnly ? ['aspect-square'] : []

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses,
    ...iconOnlyClasses
  ]
})

const iconSize = computed(() => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6'
  }
  return sizes[props.size]
})

const iconColorClass = computed(() => {
  if (props.variant === 'secondary' || props.variant === 'ghost') {
    return 'text-gray-500 dark:text-gray-400'
  }
  if (props.variant === 'outline') {
    return 'text-blue-600 dark:text-blue-400'
  }
  return 'text-white'
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("button", {
    type: _ctx.type,
    disabled: _ctx.disabled || _ctx.loading,
    class: _normalizeClass(buttonClasses.value),
    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('click')))
  }, [
    (_ctx.loading)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(LoaderIcon)), {
          key: 0,
          class: _normalizeClass(["animate-spin -ml-1 mr-3 h-5 w-5", iconColorClass.value])
        }, null, 8, ["class"]))
      : (_ctx.icon)
        ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), {
            key: 1,
            class: _normalizeClass([iconSize.value, iconColorClass.value]),
            style: _normalizeStyle(_ctx.iconOnly ? {} : { marginRight: '0.75rem' })
          }, null, 8, ["class", "style"]))
        : _createCommentVNode("", true),
    (!_ctx.iconOnly)
      ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_ctx.loading ? _ctx.loadingText : _ctx.text), 1))
      : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})